/**
 * /equipment/radiation — the register of radioactive sealed sources, the
 * SHARED radiation module (fieldiq-api#1334, RAD-1). A row opens the source on
 * its own page (RadiationSourceScreen); dosimetry is the second tab.
 *
 * SHARED, NOT A VERTICAL. No `hasVertical` guard, matching the API, which mounts
 * /api/v1/radiation with no vertical guard and a role gate on every route. The
 * way in is the band button on /equipment. A field hand's tree registers the
 * same routes but has no door yet: it lands with the first consumer (WL-2 /
 * RAD-N), so it is not shown to every tenant that holds no source.
 *
 * Every figure here is the API's: current activity (decayed in code to the
 * tenant's today), leak-test status and days left. Nothing is computed. FILTER
 * CHIPS live in the URL (`?status=`, `?leak=`) so the back arrow from a source
 * returns to the list as it was left. THEIR COUNTS ARE A TALLY OF THE LOADED
 * ROWS: GET /sources is unpaginated and no endpoint returns a per-status count.
 * The summary line above them is GET /status, the API's own counts.
 */
import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Loader2, Plus, Settings2 } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { useCreateRadSource, useRadCatalog, useRadSources, useRadStatus } from '../../hooks/useRadiation';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  RAD_EVENT_LABEL,
  RAD_FORM_LABEL,
  RAD_LEAK_LABEL,
  RAD_LIST,
  RAD_SETTINGS,
  RAD_STATUS_LABEL,
  RAD_STATUS_RAIL,
  canAdminRad,
  isotopeLabel,
  radDaysLeft,
  radLabel,
  radQty,
} from '../../lib/radiation';
import type { RadSource } from '../../types/radiation';
import { RadLeakBadge, RadSectionNav, RadSourceDialog, RadStatusBadge } from '../../components/radiation/RadBits';
import { DH_BAND_SECONDARY, DhEmpty, DhPanel } from '../downhole/DownholeBits';
import { useOpenRecord } from '../downhole/recordPage';

const ALLOWED = 'admins, office staff, supervisors, dispatchers, field workers and logistics';

/** "Ir-192 · S/N 1234", or `—` parts. */
export const radIdentity = (s: Pick<RadSource, 'serialNumber'>) => (s.serialNumber ? `S/N ${s.serialNumber}` : '—');

/** Who holds it, from the API's custody. */
export function radCustodyText(s: RadSource): string {
  if (s.custody.checkedOut) return s.custody.holderName ? `With ${s.custody.holderName}` : 'Checked out';
  return 'Not checked out';
}

function Chips({
  label,
  options,
  active,
  onPick,
  tally,
  showCounts,
}: {
  label: string;
  options: Array<{ key: string; label: string }>;
  active: string;
  onPick: (key: string) => void;
  tally: (key: string) => number;
  showCounts: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={label}>
      {options.map((f) => (
        <button
          key={f.key}
          type="button"
          aria-pressed={active === f.key}
          onClick={() => onPick(f.key)}
          className={`inline-flex items-center gap-2 rounded-fi-pill border px-3 py-1.5 text-sm font-bold transition ${
            active === f.key ? 'border-fi-amber bg-fi-signal-amber text-fi-signal-ink' : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-text hover:bg-fieldiq-dark/60'
          }`}
        >
          {f.label}
          {showCounts && <span className="font-mono text-xs tabular-nums">{tally(f.key)}</span>}
        </button>
      ))}
    </div>
  );
}

/** The API's summary counts in one line; an absent count reads `—`. */
function StatusLine() {
  const q = useRadStatus();
  const s = q.data?.status;
  if (q.isLoading || q.isError || !s) return null;
  const n = (v: number | null) => (v === null ? '—' : String(v));
  const parts: Array<[string, string, boolean]> = [
    ['Held', n(s.held), false],
    ['Checked out', n(s.checkedOut), false],
    ['Out of shield', n(s.outOfShield), false],
    ['Leak test due soon', n(s.leakTests.dueSoon), false],
    ['Leak test overdue', n(s.leakTests.overdue), (s.leakTests.overdue ?? 0) > 0],
    ['Leak test failed', n(s.leakTests.failed), (s.leakTests.failed ?? 0) > 0],
    ['Lost in hole', n(s.lostInHole), (s.lostInHole ?? 0) > 0],
  ];
  return (
    <dl data-testid="rad-status-line" className="flex flex-wrap gap-x-6 gap-y-2 rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 text-sm">
      {parts.map(([label, value, alarm]) => (
        <div key={label} className="flex items-baseline gap-2">
          <dt className="text-xs font-bold uppercase tracking-[0.08em] text-fieldiq-muted">{label}</dt>
          <dd data-testid={`rad-stat-${label}`} className={`font-mono tabular-nums ${alarm ? 'font-black text-fieldiq-text underline decoration-fi-signal-red decoration-2' : 'text-fieldiq-text'}`}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export default function RadiationScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const elevated = useCanWriteCompanySettings();
  const canAdmin = canAdminRad(user?.role, elevated);
  const [adding, setAdding] = useState(false);

  const query = useRadSources();
  const catalogQuery = useRadCatalog();
  const create = useCreateRadSource();
  const failure = queryLoadFailure(query);
  const all = useMemo(() => query.data?.rows ?? [], [query.data]);
  const meta = query.data?.meta ?? null;
  const catalog = catalogQuery.data ?? null;

  const statusKeys = catalog?.sourceStatuses ?? Object.keys(RAD_STATUS_LABEL);
  const leakKeys = catalog?.leakStatuses ?? Object.keys(RAD_LEAK_LABEL);
  const statusFilters = [{ key: 'all', label: 'All' }, ...statusKeys.map((k) => ({ key: k, label: radLabel(RAD_STATUS_LABEL, k) }))];
  const leakFilters = [{ key: 'all', label: 'Any leak test' }, ...leakKeys.map((k) => ({ key: k, label: radLabel(RAD_LEAK_LABEL, k) }))];

  const statusParam = params.get('status');
  const leakParam = params.get('leak');
  const status = statusParam && statusFilters.some((f) => f.key === statusParam) ? statusParam : 'all';
  const leak = leakParam && leakFilters.some((f) => f.key === leakParam) ? leakParam : 'all';

  const rows = all.filter((s) => (status === 'all' || s.status === status) && (leak === 'all' || s.leakTest.status === leak));
  const tallyStatus = (k: string) => (k === 'all' ? all.length : all.filter((s) => s.status === k).length);
  const tallyLeak = (k: string) => (k === 'all' ? all.length : all.filter((s) => s.leakTest.status === k).length);

  const setParam = (name: string) => (key: string) => {
    const next = new URLSearchParams(params);
    if (key === 'all') next.delete(name);
    else next.set(name, key);
    setParams(next, { replace: true });
  };

  const open = (id: string) => openRecord(`${RAD_LIST}/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };
  const activityUnit = meta?.units.activity ?? '';
  const ready = !query.isLoading && !failure;

  return (
    <div data-testid="radiation-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Equipment"
        title="Radioactive sources"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdmin ? (
            <>
              <Link to={RAD_SETTINGS} className={DH_BAND_SECONDARY}>
                <Settings2 className="size-4" aria-hidden="true" />
                Settings
              </Link>
              <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
                <Plus className="size-4" aria-hidden="true" />
                Register source
              </button>
            </>
          ) : undefined
        }
        /* No stat strip: the API's counts sit in the body (StatusLine). */
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <RadSectionNav />
          <StatusLine />
          <Chips label="Filter by source status" options={statusFilters} active={status} onPick={setParam('status')} tally={tallyStatus} showCounts={ready} />
          <Chips label="Filter by leak test" options={leakFilters} active={leak} onPick={setParam('leak')} tally={tallyLeak} showCounts={ready} />

          <DhPanel
            eyebrow="Sealed sources"
            title="Sources"
            testId="rad-register"
            footer={
              ready && all.length > 0
                ? `Showing ${rows.length} of ${all.length}${canAdmin ? '' : ' · only an admin or office staff can register a source or record a leak test'}`
                : undefined
            }
          >
            {query.isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading sources" />
              </div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="radioactive sources" allowed={ALLOWED} />
            ) : all.length === 0 ? (
              <DhEmpty>
                No radioactive source is registered yet.
                {canAdmin && ' Register one with Register source.'}
              </DhEmpty>
            ) : rows.length === 0 ? (
              <DhEmpty>No source matches these filters.</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1000px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Source</th>
                        <th scope="col" className="px-4 py-3">Form</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3">Activity today</th>
                        <th scope="col" className="px-4 py-3">Leak test</th>
                        <th scope="col" className="px-4 py-3">Custody</th>
                        <th scope="col" className="px-4 py-3">Last event</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((s) => (
                        <tr
                          key={s.id}
                          data-testid="rad-row"
                          role="button"
                          tabIndex={0}
                          onClick={() => open(s.id)}
                          onKeyDown={rowKeys(s.id)}
                          className={`cursor-pointer border-l-4 border-t border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${RAD_STATUS_RAIL[s.status] ?? 'border-l-transparent'}`}
                        >
                          <td className="px-4 py-3">
                            <span className="block text-sm font-extrabold text-white">{isotopeLabel(catalog, s.isotope, s.isotopeLabel)}</span>
                            <span className="block text-xs text-fieldiq-muted">{radIdentity(s)}</span>
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{radLabel(RAD_FORM_LABEL, s.sourceForm)}</td>
                          <td className="px-4 py-3"><RadStatusBadge status={s.status} /></td>
                          <td className="px-4 py-3 font-mono text-sm tabular-nums text-fieldiq-text" data-testid="rad-activity">
                            {radQty(s.currentActivity, activityUnit)}
                          </td>
                          <td className="px-4 py-3">
                            <span className="flex flex-col gap-1">
                              <RadLeakBadge status={s.leakTest.status} />
                              <span className="font-mono text-xs tabular-nums text-fieldiq-muted">{radDaysLeft(s.leakTest.daysLeft)}</span>
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{radCustodyText(s)}</td>
                          <td className="px-4 py-3 text-sm text-fieldiq-text">{radLabel(RAD_EVENT_LABEL, s.lastEvent?.event)}</td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="space-y-3 p-4 md:hidden">
                  {rows.map((s) => (
                    <div
                      key={s.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(s.id)}
                      onKeyDown={rowKeys(s.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <p className="flex flex-wrap items-center gap-2 text-sm font-extrabold text-white">
                        {isotopeLabel(catalog, s.isotope, s.isotopeLabel)}
                        <RadStatusBadge status={s.status} />
                      </p>
                      <p className="mt-1 text-xs text-fieldiq-muted">{radIdentity(s)} · {radCustodyText(s)}</p>
                      <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-fieldiq-text">
                        {radQty(s.currentActivity, activityUnit)} · <RadLeakBadge status={s.leakTest.status} /> {radDaysLeft(s.leakTest.daysLeft)}
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>

      {canAdmin && (
        <RadSourceDialog
          open={adding}
          onClose={() => setAdding(false)}
          catalog={catalog}
          activityUnit={activityUnit}
          source={null}
          pending={create.isPending}
          onSubmit={async (body) => {
            const created = await create.mutateAsync(body);
            if (created?.id) open(created.id);
          }}
        />
      )}
    </div>
  );
}
